import { Product } from '@components/ui/Product';
import { BatchSelect } from '@modules/Batches/components/BatchSelect';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LocationsByProductId } from '../../hooks/useReceiveProductLocations';
import { IdentifiedOrder } from '../../models';

interface OrderProductsListProps {
  order: IdentifiedOrder;
  scannedQuantities: Record<string, number>;
  selectedBatchIds?: Record<number, number | null>;
  onBatchChange?: (productId: number, batchId: number | null) => void;
  locationsByProductId?: LocationsByProductId;
  isLoadingLocations?: boolean;
}

// Función auxiliar para construir URL completa de imagen
const buildImageUrl = (imageHash?: string) => {
  if (!imageHash) return '';
  if (imageHash.startsWith('http')) return imageHash;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
};

export const OrderProductsList: React.FC<OrderProductsListProps> = ({
  order,
  scannedQuantities,
  selectedBatchIds = {},
  onBatchChange,
  locationsByProductId,
  isLoadingLocations = false,
}) => {
  const { t } = useTranslation('reusedModule');
  const processOrderProducts = () => {
    if (!order.lines) return [];

    // Agrupar líneas por product_id (expandir packs a productos individuales)
    const productGroups = order.lines.reduce(
      (acc, line) => {
        // Si la línea tiene un pack, expandir a productos individuales
        if (line.pack && line.pack.lines) {
          const packQty = line.qty; // Cantidad de packs

          line.pack.lines.forEach((packLine) => {
            const productId = packLine.product_id;
            if (!productId) return;

            // Calcular cantidad esperada: cantidad del pack * cantidad del producto en el pack
            const expectedQty = packQty * packLine.qty;

            if (!acc[productId]) {
              acc[productId] = {
                productId,
                name: packLine.product?.name || `Producto ID: ${productId}`,
                code: packLine.product?.code || '',
                image: buildImageUrl(packLine.product?.image || packLine.product?.image_hash),
                lines: [],
                totalExpectedQuantity: 0,
                totalScannedQuantity: 0,
                packInfo: line.pack.title, // Guardar info del pack para mostrar
              };
            }

            acc[productId].lines.push({
              ...line,
              packLine, // Guardar referencia a la línea del pack
            });
            acc[productId].totalExpectedQuantity += expectedQty;

            // Para packs, necesitamos rastrear escaneos por producto individual
            // La clave incluye el pack_id y el product_id del producto dentro del pack
            const scannedKey = `${order.id}:${line.id}:${productId}`;
            const scannedQty = scannedQuantities[scannedKey] || 0;
            acc[productId].totalScannedQuantity += scannedQty;
          });
        } else if (line.product_id) {
          // Producto individual (no pack)
          const productId = line.product_id;

          if (!acc[productId]) {
            acc[productId] = {
              productId,
              name: line.product?.name || line.name || `Producto ID: ${productId}`,
              code: line.product?.code || '',
              image: buildImageUrl(line.product?.image || line.product?.image_hash),
              lines: [],
              totalExpectedQuantity: 0,
              totalScannedQuantity: 0,
              packInfo: null,
            };
          }

          acc[productId].lines.push(line);
          acc[productId].totalExpectedQuantity += line.qty;

          // Sumar las cantidades escaneadas de todas las líneas de este producto
          const scannedKey = `${order.id}:${line.id}`;
          const scannedQty = scannedQuantities[scannedKey] || 0;
          acc[productId].totalScannedQuantity += scannedQty;
        }

        return acc;
      },
      {} as Record<
        number,
        {
          productId: number;
          name: string;
          code: string;
          image: string;
          lines: any[];
          totalExpectedQuantity: number;
          totalScannedQuantity: number;
          packInfo: string | null;
        }
      >,
    );

    // Convertir a array y calcular si está completado
    return Object.values(productGroups).map((group) => ({
      id: `${order.id}-product-${group.productId}`,
      productId: group.productId,
      name: group.name,
      code: group.code,
      image: group.image,
      expectedQuantity: group.totalExpectedQuantity,
      scannedQuantity: group.totalScannedQuantity,
      isCompleted: group.totalScannedQuantity >= group.totalExpectedQuantity,
      lineCount: group.lines.length,
      packInfo: group.packInfo, // Info del pack si viene de un pack
    }));
  };

  const products = processOrderProducts();
  const completedProducts = products.filter((p) => p.isCompleted);
  const pendingProducts = products.filter((p) => !p.isCompleted);

  const renderProductLocationChips = (productId: number) => {
    if (!locationsByProductId || isLoadingLocations) return null;
    const locs = locationsByProductId[productId];
    if (!locs?.length) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-3 w-full" data-testid={`receive-product-locations-${productId}`}>
        {locs.map((loc) => (
          <div
            key={loc.id}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-foreground transition-colors"
          >
            <span className="font-medium">{loc.code}</span>
            <span className="text-muted-foreground">· {loc.stock}</span>
          </div>
        ))}
      </div>
    );
  };

  if (products.length === 0) {
    return (
      <div className="bg-background border border-border rounded-lg p-4">
        <div className="text-center py-8">
          <div className="text-muted-foreground mb-4">
            <svg
              className="mx-auto h-12 w-12 text-muted-foreground/50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">No hay productos</h3>
          <p className="text-sm text-muted-foreground">Este pedido no tiene productos asignados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isLoadingLocations && (
        <p className="text-sm text-muted-foreground" data-testid="receive-locations-loading">
          {t('summary.locations_loading')}
        </p>
      )}

      {/* Productos pendientes */}
      {pendingProducts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="text-orange-600 dark:text-orange-400">Por escanear</span>
              <span className="text-sm text-muted-foreground">({pendingProducts.length})</span>
            </h3>
          </div>

          <div className="space-y-4">
            {pendingProducts.map((product) => {
              const remaining = product.expectedQuantity - product.scannedQuantity;
              const progressPercentage = (product.scannedQuantity / product.expectedQuantity) * 100;

              return (
                <div key={product.id} className="bg-background border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-2">
                        <Product
                          id={product.productId}
                          src={product.image}
                          name={product.name}
                          code={product.code}
                          acronym={product.name?.charAt(0).toUpperCase()}
                          showImageModal={true}
                          enableLineClamp={true}
                        />
                        {product.packInfo && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                            Pack: {product.packInfo}
                          </span>
                        )}
                      </div>
                      {onBatchChange && (
                        <BatchSelect
                          productId={product.productId}
                          value={selectedBatchIds[product.productId] ?? null}
                          onChange={(id) => onBatchChange(product.productId, id)}
                        />
                      )}
                    </div>

                    {/* Información de cantidades */}
                    <div className="flex flex-col items-end space-y-2 ml-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                          {product.scannedQuantity}
                        </span>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-lg font-semibold text-muted-foreground">{product.expectedQuantity}</span>
                      </div>

                      <div className="text-xs text-muted-foreground">Faltan {remaining}</div>

                      {/* Barra de progreso */}
                      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-orange-500 transition-all duration-300"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  {renderProductLocationChips(product.productId)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Productos completados */}
      {completedProducts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="text-green-600 dark:text-green-400">Escaneados</span>
              <span className="text-sm text-muted-foreground">({completedProducts.length})</span>
            </h3>
          </div>

          <div className="space-y-4">
            {completedProducts.map((product) => (
              <div
                key={product.id}
                className="bg-green-50/30 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-2">
                      <Product
                        id={product.productId}
                        src={product.image}
                        name={product.name}
                        code={product.code}
                        acronym={product.name?.charAt(0).toUpperCase()}
                        showImageModal={true}
                        enableLineClamp={true}
                      />
                      {product.packInfo && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                          Pack: {product.packInfo}
                        </span>
                      )}
                    </div>
                    {onBatchChange && (
                      <BatchSelect
                        productId={product.productId}
                        value={selectedBatchIds[product.productId] ?? null}
                        onChange={(id) => onBatchChange(product.productId, id)}
                      />
                    )}
                  </div>

                  {/* Información de cantidades completadas */}
                  <div className="flex flex-col items-end space-y-2 ml-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {product.scannedQuantity}
                      </span>
                      <span className="text-muted-foreground">/</span>
                      <span className="text-lg font-semibold text-muted-foreground">{product.expectedQuantity}</span>
                    </div>

                    <div className="text-xs text-green-600 dark:text-green-400 font-medium">✓ Completado</div>

                    {/* Barra de progreso completada */}
                    <div className="w-16 h-2 bg-green-200 dark:bg-green-800 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500" style={{ width: '100%' }} />
                    </div>
                  </div>
                </div>
                {renderProductLocationChips(product.productId)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resumen del pedido */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">Progreso del pedido:</span>
          <span className="font-medium text-foreground">
            {completedProducts.length} de {products.length} productos escaneados
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total de unidades:</span>
          <span className="font-medium text-foreground">
            {products.reduce((sum, p) => sum + p.scannedQuantity, 0)} de{' '}
            {products.reduce((sum, p) => sum + p.expectedQuantity, 0)} unidades
          </span>
        </div>

        <div className="mt-2">
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${products.length > 0 ? (completedProducts.length / products.length) * 100 : 0}%`,
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};
